import { PaginateSortDirection } from '@/__generated__/graphql';
import { Accordion } from '@/components/basicComponents/accordion';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { SectionZeroState } from '@/components/basicComponents';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import {
  EditInvestorPreviewFieldItem,
  EditInvestorPreviewFields,
  EditInvestorPreviewFieldTitle,
  EditInvestorPreviewFieldValue
} from '@/components/investors-page/editInvestorPage/styles';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { createAddressSchema, createPhoneNumberSchema, ReportingType as ReportingTypeEnum, ROLE, TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { z } from 'zod/v4';
import { getSettingsItemValue, SettingsContainer } from '../..';
import { SYSTEM_SETTINGS_QUERY, TENANT_NAME_QUERY } from '../../queries';
import { reportingTypesEnumMap } from '../../tenants/constants';
import { formatUserPhoneNumber } from '../../users/firmTable/tableBody';
import { LIST_ADVISORY_FIRM_USERS_QUERY, LIST_PLATFORM_MANAGER_USERS_QUERY } from '../../users/queries';
import { IUser } from '../../users/types';
import { ISetting } from '../templatesSettings/types';
import { EditBasicInfoModalPage } from './modalPages/editBasicInfoModalPage';
import { EditContactInfoModalPage } from './modalPages/editContactInfoModalPage';
import { EditDefaultUsersModalPage } from './modalPages/editDefaultUsersModalPage';
import { FirmInfoSectionNameEnum, IFirmInfoFormData, IFirmInfoSectionsData, userRoleMap } from './types';

const firmInfoSchema = z.object({
  firmName: z.string({ error: 'This field is required' }).min(1, { error: 'This field is required' }),
  reportingType: z.enum([...Object.values(ReportingTypeEnum), '']).optional(),
  email: z.email().or(z.literal('')).nullish(),
  website: z.string().or(z.literal('')).nullish(),
  phone: createPhoneNumberSchema({}, true).nullish(),
  primaryAdmin: z.string({ error: 'This field is required' }),
  advisorTeamDefault: z.string({ error: 'This field is required' }),
  investmentTeamDefault: z.string({ error: 'This field is required' }),
  operationsTeamDefault: z.string({ error: 'This field is required' }),
  complianceTeamDefault: z.string({ error: 'This field is required' }),
  address: createAddressSchema({}, true).nullish()
});

type FirmInfoSchemaType = z.infer<typeof firmInfoSchema>;

// Define custom types for phone and address
interface PhoneType {
  countryCode: string;
  number: string;
}

// Use the trace8-schema's address type structure
interface AddressType {
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
}

// Create a new type that uses the Zod inferred type for most fields
// but replaces phone and address with our custom types
export type FirmInfoType = Omit<FirmInfoSchemaType, 'phone' | 'address'> & {
  phone?: PhoneType | null;
  address?: AddressType | null;
};

interface FirmInfoProps {
  backToTitle: string;
  tenantId?: string;
  handleClose: () => void;
}

export const FirmInfo = ({ backToTitle, tenantId, handleClose }: FirmInfoProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { searchParams, setSearchParams } = useSearchParamsManager();

  const modalPageType: FirmInfoSectionNameEnum | null = (searchParams.get('modalPageType') as FirmInfoSectionNameEnum) || null;

  const isSuperAdmin = user?.roles.includes(ROLE.SUPER_ADMIN);
  const isAdvisoryFirmUser = user?.tenant?.type === TenantType.ADVISORY_FIRM;

  const [firmInfoId, setFirmInfoId] = useState<string | null>(null);
  const [reportingTypeId, setReportingTypeId] = useState<string | null>(null);
  const [firmInfoSectionsData, setFirmInfoSectionsData] = useState<IFirmInfoSectionsData[]>([]);
  const [currentFormData, setCurrentFormData] = useState<IFirmInfoSectionsData[keyof IFirmInfoSectionsData] | null>(null);
  const [usersSelectOptions, setUsersSelectOptions] = useState<Record<keyof typeof userRoleMap, ISelectValue[]>>({
    primaryAdmin: [],
    advisorTeamDefault: [],
    investmentTeamDefault: [],
    operationsTeamDefault: [],
    complianceTeamDefault: []
  });

  const {
    data: firmInfoData,
    loading: firmInfoLoading,
    refetch: firmInfoRefetch
  } = useQuery(SYSTEM_SETTINGS_QUERY, {
    variables: {
      data: {
        ...((isAdvisoryFirmUser || tenantId) && { tenantId: tenantId || user?.tenant?.id }),
        key: 'firmInfo',
        type: 'config',
        ...((isAdvisoryFirmUser || tenantId) && { allowNullTenantSettings: true })
      }
    }
  });

  const {
    data: featureFlagData,
    loading: featureFlagLoading,
    refetch: featureFlagRefetch
  } = useQuery(SYSTEM_SETTINGS_QUERY, {
    skip: !tenantId,
    variables: {
      data: {
        tenantId,
        type: 'featureFlag',
        allowNullTenantSettings: true
      }
    }
  });

  const {
    data: reportingTypeData,
    loading: reportingTypeLoading,
    refetch: reportingTypeRefetch
  } = useQuery(SYSTEM_SETTINGS_QUERY, {
    variables: {
      data: {
        tenantId: tenantId || user?.tenant?.id,
        type: 'appSetting',
        key: 'moduleConfig'
      }
    }
  });

  const {
    data: tenantNameData,
    loading: tenantNameLoading,
    refetch: tenantNameRefetch
  } = useQuery(TENANT_NAME_QUERY, {
    variables: {
      id: tenantId || user?.tenant?.id
    }
  });

  const {
    data: listUsersData,
    loading: listUsersLoading,
    refetch: listUsersRefetch
  } = useQuery<{
    listPlatformManagerUsers?: { total: number; page: number; perPage: number; data: IUser[] };
    listAdvisoryFirmUsers?: { total: number; page: number; perPage: number; data: IUser[] };
  }>(tenantId || isAdvisoryFirmUser ? LIST_ADVISORY_FIRM_USERS_QUERY : LIST_PLATFORM_MANAGER_USERS_QUERY, {
    variables: {
      filters: {
        ...(tenantId && { tenantId }),
        sortBy: 'firstName',
        sortDirection: PaginateSortDirection.Asc,
        limit: 1000,
        page: 1,
        viewSuperAdminsOverride: true
      }
    }
  });

  const loadingStates = [firmInfoLoading, featureFlagLoading, reportingTypeLoading, tenantNameLoading, listUsersLoading];

  const isLoading = loadingStates.some((loading) => loading);

  useEffect(() => {
    if (isLoading) return;

    const internalReviewFlag = featureFlagData?.systemSettings?.find((setting) => setting?.key === 'internalReview');
    const internalReviewFlagValue = internalReviewFlag?.value === 'true' || false;
    const bulkAllocateFlag = featureFlagData?.systemSettings?.find((setting) => setting?.key === 'bulkAllocate');
    const bulkAllocateFlagValue = bulkAllocateFlag?.value === 'true' || false;
    const firmInfoId = firmInfoData?.systemSettings?.find((setting) => setting.key === 'firmInfo')?.id ?? null;
    setFirmInfoId(firmInfoId);

    const firmInfo = getSettingsItemValue(firmInfoData?.systemSettings ?? [], 'firmInfo') || {};
    const firmName = tenantNameData?.tenant?.name || '';
    const { email, website, phone, address } = firmInfo || {};

    if (isSuperAdmin) {
      const reportingTypeId = reportingTypeData?.systemSettings?.find((setting: ISetting) => setting.key === 'moduleConfig')?.id ?? null;
      setReportingTypeId(reportingTypeId);
    }

    const reportingTypeSettingsItem = getSettingsItemValue(reportingTypeData?.systemSettings ?? [], 'moduleConfig') || { Reporting: '' };
    const reportingType = reportingTypeSettingsItem?.Reporting
      ? ReportingTypeEnum[getEnumKeyByValue(ReportingTypeEnum, reportingTypeSettingsItem.Reporting) as keyof typeof ReportingTypeEnum]
      : '';

    const users = tenantId || isAdvisoryFirmUser ? listUsersData.listAdvisoryFirmUsers?.data : listUsersData.listPlatformManagerUsers?.data;
    const currentDefaultUsers = {
      primaryAdmin: users.find((user) => user.roles.includes(getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN))),
      advisorTeamDefault: users.find((user) => user.roles.includes(getEnumKeyByValue(ROLE, ROLE.ADVISOR_TEAM_DEFAULT))),
      investmentTeamDefault: users.find((user) => user.roles.includes(getEnumKeyByValue(ROLE, ROLE.INVESTMENT_TEAM_DEFAULT))),
      operationsTeamDefault: users.find((user) => user.roles.includes(getEnumKeyByValue(ROLE, ROLE.OPERATIONS_TEAM_DEFAULT))),
      complianceTeamDefault: users.find((user) => user.roles.includes(getEnumKeyByValue(ROLE, ROLE.COMPLIANCE_TEAM_DEFAULT)))
    };
    const { primaryAdmin, advisorTeamDefault, investmentTeamDefault, operationsTeamDefault, complianceTeamDefault } = currentDefaultUsers;

    setFirmInfoSectionsData([
      {
        title: 'Basic Info',
        name: FirmInfoSectionNameEnum.BasicInfo,
        modalPageType: FirmInfoSectionNameEnum.BasicInfo,
        isEmpty: !firmName && !reportingType,
        formData: {
          firmName: firmName,
          reportingType,
          internalReviewFlag: internalReviewFlagValue,
          bulkAllocateFlag: bulkAllocateFlagValue,
          internalReviewFlagId: internalReviewFlag?.id ?? null,
          bulkAllocateFlagId: bulkAllocateFlag?.id ?? null
        },
        displayData: [
          { title: 'Firm Name', value: firmName },
          { title: 'Reporting Template', value: reportingType ? reportingTypesEnumMap[reportingType] : '' },
          ...(!!tenantId
            ? [
                { title: 'Bulk Allocator', value: bulkAllocateFlagValue ? 'On' : 'Off' },
                { title: 'Internal Review', value: bulkAllocateFlagValue ? (internalReviewFlagValue ? 'On' : 'Off') : '' }
              ]
            : [])
        ]
      },
      {
        title: 'Default Users',
        name: FirmInfoSectionNameEnum.DefaultUsers,
        modalPageType: FirmInfoSectionNameEnum.DefaultUsers,
        isEmpty: !primaryAdmin && !investmentTeamDefault && !advisorTeamDefault && !operationsTeamDefault && !complianceTeamDefault,
        formData: {
          primaryAdmin: primaryAdmin?.id,
          advisorTeamDefault: advisorTeamDefault?.id,
          investmentTeamDefault: investmentTeamDefault?.id,
          operationsTeamDefault: operationsTeamDefault?.id,
          complianceTeamDefault: complianceTeamDefault?.id
        },
        displayData: [
          { title: 'Primary Admin', value: primaryAdmin ? `${primaryAdmin.firstName} ${primaryAdmin.lastName}` : '' },
          { title: 'Investment Team', value: investmentTeamDefault ? `${investmentTeamDefault.firstName} ${investmentTeamDefault.lastName}` : '' },
          { title: 'Advisor Team', value: advisorTeamDefault ? `${advisorTeamDefault.firstName} ${advisorTeamDefault.lastName}` : '' },
          { title: 'Operations Team', value: operationsTeamDefault ? `${operationsTeamDefault.firstName} ${operationsTeamDefault.lastName}` : '' },
          { title: 'Compliance Team', value: complianceTeamDefault ? `${complianceTeamDefault.firstName} ${complianceTeamDefault.lastName}` : '' }
        ]
      },
      {
        title: 'Contact Info',
        name: FirmInfoSectionNameEnum.ContactInfo,
        modalPageType: FirmInfoSectionNameEnum.ContactInfo,
        isEmpty: !email && !website && !phone && !address,
        formData: {
          email,
          website,
          phone,
          address
        },
        displayData: [
          { title: 'Street Address', value: address?.streetAddress },
          { title: 'City', value: address?.city },
          { title: 'State', value: address?.state },
          { title: 'Zip', value: address?.zipCode },
          { title: 'Country', value: address?.country },
          { title: 'Phone', value: formatUserPhoneNumber(phone?.countryCode, phone?.number) },
          { title: 'Website', value: website },
          { title: 'Email', value: email }
        ]
      }
    ]);

    const usersSelectOptions = Object.fromEntries(
      Object.entries(userRoleMap).map(([key, roles]) => [
        key as keyof typeof userRoleMap,
        users
          .filter((user) => user.roles.some((role) => roles.includes(role)))
          .map((user) => createSimpleSelectOption(user.id, `${user.firstName} ${user.lastName}`))
      ])
    ) as Record<keyof typeof userRoleMap, ISelectValue[]>;

    setUsersSelectOptions(usersSelectOptions);
  }, [isLoading, firmInfoData, featureFlagData, tenantNameData, reportingTypeData, listUsersData]);

  useEffect(() => {
    if (!modalPageType || !!currentFormData) return;

    const section = firmInfoSectionsData?.find((section) => section.modalPageType === modalPageType);
    if (section) {
      setCurrentFormData(section.formData);
    }
  }, [modalPageType, firmInfoSectionsData]);

  const handleModalPage = ({ type, formData }: { type: FirmInfoSectionNameEnum | null; formData?: IFirmInfoFormData[keyof IFirmInfoFormData] }) => {
    setSearchParams(
      (prev) => {
        if (type) {
          prev.set('modalPageType', type);
        } else {
          prev.delete('modalPageType');
        }
        return prev;
      },
      { replace: true }
    );
    setCurrentFormData(formData || null);
  };

  if (isLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (!!modalPageType) {
    return (
      <>
        {modalPageType === FirmInfoSectionNameEnum.BasicInfo && (
          <EditBasicInfoModalPage
            basicInfoFormData={currentFormData as IFirmInfoFormData['basicInfo']}
            reportingTypeId={reportingTypeId}
            tennantData={{ id: tenantId, name: tenantNameData?.tenant?.name || '' }}
            showFeatureFlags={!!tenantId}
            featureFlagRefetch={featureFlagRefetch}
            reportingTypeRefetch={reportingTypeRefetch}
            tenantNameRefetch={tenantNameRefetch}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === FirmInfoSectionNameEnum.DefaultUsers && (
          <EditDefaultUsersModalPage
            defaultUsersFormData={currentFormData as IFirmInfoFormData['defaultUsers']}
            usersSelectOptions={usersSelectOptions}
            tenantId={tenantId}
            listUsersRefetch={listUsersRefetch}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === FirmInfoSectionNameEnum.ContactInfo && (
          <EditContactInfoModalPage
            contactInfoFormData={currentFormData as IFirmInfoFormData['contactInfo']}
            firmInfoId={firmInfoId}
            tenantId={tenantId}
            firmInfoRefetch={firmInfoRefetch}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
      </>
    );
  }

  return (
    <>
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
        <PageTitle title="Firm Info" />
      </MainWrap>
      <MainWrap>
        <SettingsContainer>
          <SectionsWrapper>
            {firmInfoSectionsData.map((section) => (
              <Accordion
                key={section.name}
                id={section.name}
                title={section.title}
                onEdit={() => handleModalPage({ type: section.modalPageType, formData: section.formData })}
              >
                {section?.isEmpty ? (
                  <SectionZeroState
                    button={{
                      name: 'Add Information',
                      onClick: () => handleModalPage({ type: section.modalPageType, formData: section.formData })
                    }}
                  />
                ) : (
                  <>
                    <EditInvestorPreviewFields>
                      {section.displayData.map(
                        (item) =>
                          item.value && (
                            <EditInvestorPreviewFieldItem key={item.title} isMobile={isMobile}>
                              <EditInvestorPreviewFieldTitle>{item.title}</EditInvestorPreviewFieldTitle>
                              <EditInvestorPreviewFieldValue>{item.value}</EditInvestorPreviewFieldValue>
                            </EditInvestorPreviewFieldItem>
                          )
                      )}
                    </EditInvestorPreviewFields>
                  </>
                )}
              </Accordion>
            ))}
          </SectionsWrapper>
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
